
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';

// Component Imports
import ModalContent from '@/components/modal/ModalContent.vue';
import ModalFullScreen from '@/components/modal/ModalFullScreenNew.vue';
import FormDatepicker from '@/components/form/FormDatepicker.vue';
import TimeTrackingDelete from '@/components/icons/TimeTrackingDelete.vue';
import TimeTrackingEdit from '@/components/icons/TimeTrackingEdit.vue';
import CrossIcon from "@/components/icons/CrossIconNew.vue";
import CrossIconHover from "@/components/icons/CrossIconHover.vue";
import NewTimePicker from '@/components/projects/timeTracker/NewTimePickerNew.vue';

// GraphQL Imports
import INSERT_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/InsertProjectActivityTime.gql';
import GET_PROJECT_ACTIVITY_TIMES from '@/apollo/projectActivities/GetProjectActivityTimes.gql';
import GET_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/GetRequstedHours.gql';
import UPDATE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/UpdateProjectActivityTime.gql';
import UPDATE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/ProjectActivityRequestMoreHours.gql';
import DELETE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/DeleteProjectActivityTime.gql';
import DELETE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS from '@/apollo/projectActivities/DeleteProjectActivityRequestedHours.gql';
import PROJECT_ACTIVITY_TIME_AGGREGATE from '@/apollo/projectActivities/ProjectActivityTimeAggregate.gql';
import PROJECT_ACTIVITY_REQUEST_MORE_HOURS_AGGREGATE from '@/apollo/projectActivities/ProjectActivityRequestMoreHoursAggregate.gql';
import INSERT_PROJECT_ACTIVITY_MORE_HOURS_REQUEST from '@/apollo/projectActivities/InsertProjectActivityRequestMoreHours.gql';

// Types
import {
	Project_Activity,
	Project_Activity_Time,
	Project_Activity_Request_More_Hours,
	User,
} from '@/generated/graphql';

// Utils
import convertSecondsToReadable from '@/utils/convertSecondsToReadable';
import { NOTIFICATION } from '@/entities/Events';
import Message from '@/data/notificationsMessages';

// Form Actions Component
const FormActions = Vue.component('FormActions', {
	props: {
		canSave: Boolean,
		processing: Boolean,
		buttonText: String,
	},
	template: `
	  <div class="flex flex-row gap-4 mt-5 xl:mt-0">
		<button
		  class="outlineTimeButton buttonHover"
		  @click="$emit('cancel')"
		  :disabled="processing"
		>
		  Cancel
		</button>
		<button
		  :disabled="!canSave"
		  type="button"
		  class="blackTimeButton buttonHover"
		  @click="$emit('save')"
		  :disabled="processing"
		>
		  {{ buttonText }}
		</button>
	  </div>
	`
});

// Status Badge Component
const StatusBadge = Vue.component('StatusBadge', {
	props: {
		status: String,
	},
	computed: {
		statusLower() {
			return this.status.toLowerCase();
		}
	},
	template: `
	  <template v-if="statusLower === 'approved'">
		<p class="approved">Approved</p>
	  </template>
	  <template v-else-if="statusLower === 'rejected'">
		<p class="declined flex px-2 text-center cursor-default rounded-xl whitespace-no-wrap items-center justify-center font-normal bg-red-200 text-red-500 h-5 text-low-xs pt-0.5">
		  Declined
		</p>
	  </template>
	  <template v-else-if="statusLower === 'pending' || statusLower === 'created' || statusLower === 'submitted'">
		<p class="pending">Pending approval</p>
	  </template>
	`
});

@Component({
	components: {
		ModalContent,
		CrossIcon,
		CrossIconHover,
		TimeTrackingDelete,
		TimeTrackingEdit,
		ModalFullScreen,
		FormDatepicker,
		NewTimePicker,
		FormActions,
		StatusBadge
	},
	apollo: {
		trackedTimes: {
			query: GET_PROJECT_ACTIVITY_TIMES,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) => data.project_activity_time,
		},
		timeAggregate: {
			query: PROJECT_ACTIVITY_TIME_AGGREGATE,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) =>
				data.project_activity_time_aggregate.aggregate.sum.time,
		},
		requestedHours: {
			query: GET_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) => data.project_activity_request_more_hours,
		},
		hoursAggregate: {
			query: PROJECT_ACTIVITY_REQUEST_MORE_HOURS_AGGREGATE,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) =>
				data.project_activity_request_more_hours_aggregate.aggregate.sum
					.hours,
		},
	},
})
export default class TimeTrackerModal extends Vue {
	@Prop({ type: Object, required: true }) readonly activity!: Project_Activity;
	@Prop({ type: String, required: true }) readonly currentUserId!: string;
	@Prop({ type: Function, required: false }) checkDateWeekDisable?: Function;

	// Component state
	rangeTracker = false;
	timerTracker = true;
	manualTracker = false;
	isHovered = false;
	requestHoursSlot = false;
	activeClasses = 'font-semibold activeClass';
	rangeTrackerClasses = '';
	timerTrackerClasses = '';
	manualTrackerClasses = '';
	requestHoursClasses = '';
	start_time = '00:00:00';
	end_time = '00:00:01';
	tracked_date = new Date();
	notes = '';
	manualTimeInput = '00:00:00';
	id = '';
	status = 'CREATED';
	timerRunning = false;
	timerRecorded = false;
	processing = false;
	reason = '';
	requestTimeInput = '00:00:00';

	// Apollo-managed data
	trackedTimes: Project_Activity_Time[] = [];
	requestedHours: Project_Activity_Request_More_Hours[] = [];
	timeAggregate = 0;
	hoursAggregate = 0;

	// Timer state
	timerStart: Date = new Date();
	timerStop: Date = new Date();
	currentUser: User | null = null;

	// Utilities
	convertSecondsToReadable = convertSecondsToReadable;

	// ----------------
	// Computed Properties
	// ----------------

	get hasRunningTimer(): boolean {
		return this.showTrackingModes.title !== '';
	}

	get timeSelect(): number {
		if (this.rangeTracker) return this.rangeTime;
		if (this.manualTracker) return this.manualTime;
		if (this.timerTracker) return this.timerTime;
		if (this.requestHoursSlot) return this.requestHoursTime;
		return 0;
	}

	get getTitle(): string {
		return this.activity.title;
	}

	get canSave(): boolean {
		if (this.timerTracker && this.timerRecorded && this.timeSelect > 0) {
			return true;
		}
		if (this.manualTracker && this.timeSelect > 0) {
			return true;
		}
		if (this.rangeTracker && this.timeSelect && this.start_time < this.end_time) {
			return true;
		}
		if (this.requestHoursSlot && this.timeSelect > 0 && this.reason) {
			return true;
		}
		return false;
	}

	get buttonText(): string {
		return 'Save';
	}

	get buttonStartTimer(): string {
		return this.timerRunning ? 'Stop Time' : 'Start Time';
	}

	get rangeTime(): number {
		if (!this.start_time || !this.end_time) {
			return 0;
		}

		const start_time = this.formatTime(this.start_time);
		const end_time = this.formatTime(this.end_time);

		const start = start_time.split(':');
		const end = end_time.split(':');

		const startDate = new Date(
			0, 0, 0,
			Number(start[0] || 0),
			Number(start[1] || 0),
			Number(start[2] || 0)
		);

		const endDate = new Date(
			0, 0, 0,
			Number(end[0] || 0),
			Number(end[1] || 0),
			Number(end[2] || 0)
		);

		const diff = endDate.getTime() - startDate.getTime();

		// Update the time values with formatted versions
		this.start_time = start_time;
		this.end_time = end_time;

		return diff / 1000;
	}

	get manualTime(): number {
		if (!this.manualTimeInput) {
			return 0;
		}

		const formattedTime = this.formatTime(this.manualTimeInput);
		const times = formattedTime.split(':');

		// Update the input with formatted version
		this.manualTimeInput = formattedTime;

		return (
			Number(times[2]) +
			Number(times[1]) * 60 +
			Number(times[0]) * 60 * 60
		);
	}

	get requestHoursTime(): number {
		if (!this.requestTimeInput) {
			return 0;
		}

		const formattedTime = this.formatTime(this.requestTimeInput);
		const times = formattedTime.split(':');

		// Update the input with formatted version
		this.requestTimeInput = formattedTime;

		return (
			Number(times[2]) +
			Number(times[1]) * 60 +
			Number(times[0]) * 60 * 60
		);
	}

	get timerTime(): number {
		if (!this.timerStart || !this.timerStop) {
			return 0;
		}

		const diff = this.timerStop.getTime() - this.timerStart.getTime();
		return diff / 1000;
	}

	get showTrackingModes(): { title: string; project: string } {
		const timedActivity = localStorage.getItem('activity')
			? JSON.parse(localStorage.getItem('activity'))
			: undefined;

		if (!timedActivity || (timedActivity && timedActivity.id === this.activity.id)) {
			return { title: '', project: '' };
		}

		return {
			title: timedActivity.title,
			project: timedActivity.project && timedActivity.project.name
				? timedActivity.project.name
				: '',
		};
	}

	get end_timeHourRange(): number[] {
		if (this.start_time) {
			const start_time = this.formatTime(this.start_time);
			const startParts = start_time.split(':');
			return Array.from(
				{ length: 24 - Number(startParts[0]) },
				(_, i) => i + Number(startParts[0])
			);
		}

		return Array.from({ length: 24 }, (_, i) => i);
	}

	get end_timeMinuteRange(): number[] {
		if (this.start_time && !(this.end_time.split(':')[0] > this.start_time.split(':')[0])) {
			const start_time = this.formatTime(this.start_time);
			const startParts = start_time.split(':');
			return Array.from(
				{ length: 60 - Number(startParts[1]) },
				(_, i) => i + Number(startParts[1])
			);
		}

		return Array.from({ length: 60 }, (_, i) => i);
	}

	get end_timeSecondRange(): number[] {
		if (
			this.start_time &&
			!(this.end_time.split(':')[0] > this.start_time.split(':')[0]) &&
			!(this.end_time.split(':')[1] > this.start_time.split(':')[1])
		) {
			const start_time = this.formatTime(this.start_time);
			const startParts = start_time.split(':');
			return Array.from(
				{ length: 60 - (Number(startParts[2]) + 1) },
				(_, i) => i + (Number(startParts[2]) + 1)
			);
		}

		return Array.from({ length: 60 }, (_, i) => i);
	}

	get end_timeError(): boolean {
		if (this.start_time && this.end_time) {
			const start = this.start_time.split(':');
			const end = this.end_time.split(':');

			const sDate = new Date(
				0, 0, 0,
				Number(start[0]),
				Number(start[1]),
				Number(start[2])
			);

			const eDate = new Date(
				0, 0, 0,
				Number(end[0]),
				Number(end[1]),
				Number(end[2])
			);

			return !(eDate > sDate);
		}

		return false;
	}

	// ----------------
	// Lifecycle Methods
	// ----------------

	created(): void {
		this.currentUser = Vue.prototype.$currentUser;
		this.timerTrackerClasses = this.activeClasses;

		// Initialize timer state from localStorage
		this.initTimerStateFromStorage();

		// Initialize timer display if needed
		if (this.timerRunning && !localStorage.getItem('timerInterval')) {
			this.displayTimer();
		}

		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
			localStorage.removeItem('timerInterval');
			this.displayTimer();
		}
	}

	// ----------------
	// Methods
	// ----------------

	/**
	 * Initialize timer state from localStorage
	 */
	initTimerStateFromStorage(): void {
		// Set timer recorded state
		this.timerRecorded = localStorage.getItem('timerRecorded') === '1';

		// Set timer running state
		this.timerRunning = localStorage.getItem('timerRunning') === '1';

		// Set timer start time
		if (localStorage.getItem('startTime')) {
			this.timerStart = new Date(localStorage.getItem('startTime'));
		}

		// Set timer stop time
		if (localStorage.getItem('stopTime')) {
			this.timerStop = new Date(localStorage.getItem('stopTime'));
		}
	}

	/**
	 * Format time string to ensure HH:mm:ss format
	 */
	formatTime(time: string): string {
		return time
			.replace('HH', '00')
			.replace('mm', '00')
			.replace('ss', '00');
	}

	/**
	 * Handle style for time added sections
	 */
	isTimeAddedAlreadyStyle(
		requestHoursSlot: boolean,
		timeAggregate: number,
		hoursAggregate: number
	): string {
		const hasTimeTracked = requestHoursSlot
			? convertSecondsToReadable(hoursAggregate) !== "00:00:00"
			: convertSecondsToReadable(timeAggregate) !== "00:00:00";

		return hasTimeTracked
			? "w-auto xl:border-r-1 xl:border-gray-300 pr-4"
			: "w-full";
	}

	/**
	 * Close the modal and clean up
	 */
	closeModal(): void {
		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
			localStorage.removeItem('timerInterval');
		}

		this.$emit('close');
	}

	/**
	 * Reset page state
	 */
	resetPage(): void {
		// Reset form fields
		this.start_time = '00:00:00';
		this.end_time = '00:00:01';
		this.notes = '';
		this.tracked_date = new Date();
		this.manualTimeInput = '00:00:00';
		this.requestTimeInput = '00:00:00';
		this.id = '';
		this.status = 'CREATED';
		this.reason = '';

		// Reset timer state
		this.timerStart = new Date();
		this.timerStop = new Date();
		this.timerRunning = false;
		this.timerRecorded = false;
		this.processing = false;

		// Refresh data
		this.refetchData();
		this.discardTimer();
	}

	/**
	 * Show timer tracking panel
	 */
	showTimerSlot(): void {
		this.resetPage();
		this.timerTracker = true;
		this.rangeTracker = false;
		this.manualTracker = false;
		this.requestHoursSlot = false;

		// Update tab classes
		this.rangeTrackerClasses = '';
		this.timerTrackerClasses = this.activeClasses;
		this.manualTrackerClasses = '';
		this.requestHoursClasses = '';
	}

	/**
	 * Show manual range tracking panel
	 */
	showRangeSlot(): void {
		this.resetPage();
		this.timerTracker = false;
		this.rangeTracker = true;
		this.manualTracker = false;
		this.requestHoursSlot = false;

		// Update tab classes
		this.rangeTrackerClasses = this.activeClasses;
		this.timerTrackerClasses = '';
		this.manualTrackerClasses = '';
		this.requestHoursClasses = '';
	}

	/**
	 * Show manual time input panel
	 */
	showManualSlot(): void {
		this.resetPage();
		this.timerTracker = false;
		this.rangeTracker = false;
		this.requestHoursSlot = false;
		this.manualTracker = true;

		// Update tab classes
		this.rangeTrackerClasses = '';
		this.timerTrackerClasses = '';
		this.requestHoursClasses = '';
		this.manualTrackerClasses = this.activeClasses;
	}

	/**
	 * Show request hours panel
	 */
	showRequestHoursSlot(): void {
		this.resetPage();
		this.timerTracker = false;
		this.rangeTracker = false;
		this.manualTracker = false;
		this.requestHoursSlot = true;

		// Update tab classes
		this.rangeTrackerClasses = '';
		this.timerTrackerClasses = '';
		this.manualTrackerClasses = '';
		this.requestHoursClasses = this.activeClasses;
	}

	/**
	 * Check for time overlap with existing entries
	 */
	checkForTimeOverlap(): boolean {
		if (!this.trackedTimes || !this.trackedTimes.length) {
			return false;
		}

		let overlap = false;

		this.trackedTimes.forEach((time) => {
			if (this.id !== time.id) {
				// Skip if time or time.tracked_date is undefined
				if (!time || !time.tracked_date) {
					return;
				}

				const sameDate = moment(this.tracked_date).format('YYYY-MM-DD') === time.tracked_date;

				if (sameDate) {
					// Make sure all properties exist before comparison
					if (!time.start_time || !time.end_time || !time.project_activity) {
						return;
					}

					// Check if start time falls within an existing entry
					const startOverlap = this.start_time > time.start_time && this.start_time < time.end_time;

					// Check if end time falls within an existing entry
					const endOverlap = this.end_time > time.start_time && this.end_time < time.end_time;

					// Check if new entry completely encompasses an existing entry
					const surroundOverlap = this.start_time < time.start_time && this.end_time > time.end_time;

					if (startOverlap || endOverlap || surroundOverlap) {
						overlap = true;
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage(`Time overlap with ${time.project_activity.title || 'another task'}`)
						);
						return true;
					}
				}
			}
		});

		return overlap;
	}


	/**
 * Save time tracking entry
 */
	async saveTimeTrack(event?: Event): Promise<void> {
		if (event) {
			event.preventDefault();
		}

		// Validate that activity exists before proceeding
		if (!this.activity || !this.activity.id) {
			console.error('Activity is undefined');
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage('Activity data is missing')
			);
			return;
		}

		// Check if date is allowed
		let disableInfo = { isDisable: false, id: null };
		try {
			if (this.checkDateWeekDisable) {
				disableInfo = await this.checkDateWeekDisable(this.tracked_date, this.activity) || { isDisable: false, id: null };
			}
		} catch (error) {
			console.error('Error checking date:', error);
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage('Error validating date')
			);
			return;
		}

		const { isDisable, id } = disableInfo;

		if (isDisable) {
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage(
					`The timesheet status must be open for the period: ${this.tracked_date.toDateString()}`
				)
			);
			return;
		}

		// Check for time overlap (skip for edit mode since it might overlap with itself)
		if (!this.id && this.checkForTimeOverlap()) {
			return;
		}

		this.processing = true;

		try {
			// Determine time value based on active tracking mode
			let timeValue = this.timeSelect;
			let startTime = this.start_time;
			let endTime = this.end_time;

			// For timer mode
			if (this.timerTracker) {
				timeValue = this.timerTime;
				if (!startTime || startTime === '00:00:00') {
					startTime = '00:00:00';
					endTime = this.convertSecondsToReadable(timeValue);
				}
			}

			// For manual mode
			if (this.manualTracker) {
				timeValue = this.manualTime;
				if (!startTime || startTime === '00:00:00') {
					startTime = '00:00:00';
					endTime = this.convertSecondsToReadable(timeValue);
				}
			}

			if (!this.id) {
				// Create new time entry
				const projectActivityTime = {
					activity_id: this.activity.id,
					contractor_id: this.activity.contractor_id || this.currentUserId, // Fallback to currentUserId if contractor_id is missing
					notes: this.notes,
					time: timeValue,
					start_time: startTime,
					end_time: endTime,
					tracked_date: moment(this.tracked_date).local().format('YYYY-MM-DD'),
					timesheet_id: (this.activity as any)?.timesheet_id || id,
				};

				await this.$apollo.mutate({
					mutation: INSERT_PROJECT_ACTIVITY_TIME,
					variables: {
						projectActivityTime,
					},
				});

				this.$eventBus.emit(
					NOTIFICATION,
					Message.successMessage('Time entry successfully created')
				);
			} else {
				// Update existing time entry
				const obj = {
					time: timeValue,
					start_time: startTime,
					end_time: endTime,
					status: this.status,
					notes: this.notes,
					tracked_date: moment(this.tracked_date).local().format('YYYY-MM-DD'),
					timesheet_id: (this.activity as any)?.timesheet_id || id,
				};

				await this.$apollo.mutate({
					mutation: UPDATE_PROJECT_ACTIVITY_TIME,
					variables: {
						id: this.id,
						obj,
					},
				});

				this.$eventBus.emit(
					NOTIFICATION,
					Message.successMessage('Time entry successfully updated')
				);
			}

			// Reset the page after saving
			this.resetPage();

			// Show the timer tab after saving
			this.showTimerSlot();
		} catch (error) {
			console.error('Error saving time track:', error);
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage('Failed to save time tracking entry')
			);
		} finally {
			this.processing = false;
		}
	}

	/**
	 * Delete a tracked time entry
	 */
	deleteTrackedTime(id: string): void {
		this.$confirm({
			title: `Delete tracked time for task ${this.activity?.title}`,
			message: 'Are you sure?',
			button: {
				no: 'No',
				yes: 'Yes',
			},
			callback: async (confirm: boolean) => {
				if (confirm) {
					try {
						await this.$apollo.mutate({
							mutation: DELETE_PROJECT_ACTIVITY_TIME,
							variables: { id },
						});
						this.refetchData();
					} catch (error) {
						console.error('Error deleting tracked time:', error);
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage('Failed to delete time entry')
						);
					}
				}
			},
		});
	}

	/**
	 * Delete a requested hours entry
	 */
	deleteRequestedHours(id: string): void {
		this.$confirm({
			title: `Delete requested hours for task ${this.activity?.title}`,
			message: 'Are you sure?',
			button: {
				no: 'No',
				yes: 'Yes',
			},
			callback: async (confirm: boolean) => {
				if (confirm) {
					try {
						await this.$apollo.mutate({
							mutation: DELETE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
							variables: { id },
						});
						this.refetchData();
					} catch (error) {
						console.error('Error deleting requested hours:', error);
						this.$eventBus.emit(
							NOTIFICATION,
							Message.errorMessage('Failed to delete requested hours')
						);
					}
				}
			},
		});
	}

	/**
	 * Refetch data from API
	 */
	refetchData(): void {
		this.$apollo.queries.trackedTimes.refetch();
		this.$apollo.queries.timeAggregate.refetch();
		this.$apollo.queries.requestedHours.refetch();
		this.$apollo.queries.hoursAggregate.refetch();
		this.$emit('timesChanged');
	}

	/**
	 * Convert date string to display format
	 */
	convertDateString(date: string): string {
		const parts = date.split('-');
		return `${parts[2]}/${parts[1]}/${parts[0]}`;
	}

	/**
 * Set up edit mode for tracked time - Always opens in Manual tab
 */
	setToEdit(time: Project_Activity_Time): void {
		// Reset page but don't clear the edit data
		this.resetTimerStates();

		// Switch to manual tracking mode
		this.timerTracker = false;
		this.rangeTracker = false;
		this.manualTracker = true;
		this.requestHoursSlot = false;

		// Update tab classes
		this.rangeTrackerClasses = '';
		this.timerTrackerClasses = '';
		this.requestHoursClasses = '';
		this.manualTrackerClasses = this.activeClasses;

		// Set form data from the time entry
		this.id = time.id;
		this.notes = time.notes || '';
		this.tracked_date = new Date(time.tracked_date);
		this.status = time.status;

		// Set the manual time input to the formatted time from the entry
		this.manualTimeInput = this.convertSecondsToReadable(time.time);

		// If the entry has start and end times, set them
		if (time.start_time && time.end_time) {
			this.start_time = time.start_time;
			this.end_time = time.end_time;
		} else {
			// Create default start/end times if none exist
			this.start_time = '00:00:00';
			this.end_time = this.manualTimeInput;
		}
	}


	resetTimerStates(): void {
		this.timerRunning = false;
		this.timerRecorded = false;

		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
			localStorage.removeItem('timerInterval');
		}
	}

	/**
	 * Set up edit mode for requested hours
	 */
	setToEditRequestedHours(time: Project_Activity_Request_More_Hours): void {
		this.showRequestHoursSlot();
		this.id = time.id;
		this.reason = time.reason;
		this.status = time.status;
		this.requestTimeInput = this.convertSecondsToReadable(time.hours);
	}

	/**
	 * Start or stop the timer
	 */
	async startStopTimer(): Promise<void> {
		const timedActivity = localStorage.getItem('activity')
			? JSON.parse(localStorage.getItem('activity'))
			: undefined;

		// Check if date is allowed
		const { isDisable, id: timesheetId } = await this.checkDateWeekDisable?.(this.tracked_date, this.activity) || { isDisable: false };

		if (isDisable) {
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage(
					`The timesheet status must be open for the period: ${this.tracked_date.toDateString()}`
				)
			);
			return;
		}

		// Check if there's no active timer or if the active timer is for the current activity
		if (!timedActivity || (timedActivity && timedActivity.id === this.activity.id)) {
			if (!this.timerRunning) {
				// Start the timer
				this.startTimer(timesheetId);
			} else {
				// Stop the timer
				this.stopTimer();
			}
		} else {
			// Alert if there's another timer running
			alert(
				`A timer is already running for task '${timedActivity?.title}'.\nPlease attend to that first.`
			);
		}
	}

	/**
	 * Start the timer
	 */
	startTimer(timesheetId?: string): void {
		// Clear any existing timer
		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
		}

		this.timerRunning = true;
		this.timerStart = new Date(localStorage.getItem('startTime') || this.resetStartTime());

		if (!localStorage.getItem('timerInterval')) {
			this.displayTimer(timesheetId);
		}
	}

	/**
	 * Stop the timer
	 */
	stopTimer(): void {
		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
		}

		this.timerRunning = false;
		this.timerStart = new Date(localStorage.getItem('startTime'));
		this.timerStop = new Date();

		this.$root.$emit('timerStopped');

		localStorage.setItem('stopTime', this.timerStop.toString());
		this.timerRecorded = true;
		localStorage.setItem('timerRecorded', '1');
		localStorage.setItem('timerRunning', '0');
	}

	/**
	 * Display the timer and update the timer label every second
	 */
	displayTimer(timesheetId?: string): void {
		const timerInterval = window.setInterval(() => {
			if (document.getElementById('timerLabel')) {
				document.getElementById('timerLabel').innerHTML = this.getTimer();
			}
		}, 1000);

		localStorage.setItem('timerInterval', timerInterval.toString());

		// Store activity in localStorage
		if (!localStorage.getItem('activity')) {
			if (timesheetId) {
				localStorage.setItem('activity', JSON.stringify({
					...this.activity,
					timesheetId: timesheetId
				}));
			} else {
				localStorage.setItem('activity', JSON.stringify(this.activity));
			}
		}

		// Store tracked date
		if (!localStorage.getItem('tracked_date')) {
			localStorage.setItem('tracked_date', this.tracked_date.toString());
		}

		// Store timer owner
		if (!localStorage.getItem('timerOwner')) {
			localStorage.setItem('timerOwner' + this.currentUser.id, '1');
		}

		this.$root.$emit('timerStarted');
	}

	/**
	 * Reset start time in localStorage and return new start time
	 */
	resetStartTime(): Date {
		const startTime = new Date();
		localStorage.setItem('startTime', startTime.toString());
		localStorage.setItem('timerRecorded', '0');
		localStorage.setItem('timerRunning', '1');
		return startTime;
	}

	/**
	 * Discard the current timer
	 */
	discardTimer(): void {
		// Reset timer state
		this.timerStart = new Date();
		this.timerStop = new Date();
		this.timerRunning = false;
		this.timerRecorded = false;

		// Clear localStorage items
		localStorage.removeItem('startTime');
		localStorage.removeItem('stopTime');
		localStorage.removeItem('timerRecorded');
		localStorage.removeItem('timerRunning');
		localStorage.removeItem('activity');
		localStorage.removeItem('appTimer');
		localStorage.removeItem('timerOwner' + this.currentUser.id);
		localStorage.removeItem('tracked_date');

		// Reset timer display
		if (document.getElementById('timerLabel')) {
			document.getElementById('timerLabel').innerHTML = '00:00:00';
		}

		// Emit timer stopped event
		this.$root.$emit('timerStopped');

		// Clear interval
		if (localStorage.getItem('timerInterval')) {
			const timerInterval = Number(localStorage.getItem('timerInterval'));
			window.clearInterval(timerInterval);
			localStorage.removeItem('timerInterval');
		}
	}

	/**
	 * Get the current timer display value
	 */
	getTimer(): string {
		let secsDiff = 0;

		if (!this.timerRunning && this.timerStart && this.timerStop) {
			secsDiff = (this.timerStop.getTime() - this.timerStart.getTime()) / 1000;
		} else if (this.timerStart && (!this.timerStop || this.timerRunning)) {
			secsDiff = (new Date().getTime() - this.timerStart.getTime()) / 1000;
		}

		return this.convertSecondsToReadable(secsDiff);
	}

	/**
	 * Send a request for more hours
	 */
	async sendRequest(event): Promise<void> {
		if (event) {
			event.preventDefault();
		}
		this.processing = true;

		try {
			if (!this.id) {
				// Create new hours request
				const projectActivityRequestedHours = {
					hours: this.timeSelect,
					reason: this.reason,
					requester_id: this.currentUserId,
					activity_id: this.activity.id,
				};

				await this.$apollo.mutate({
					mutation: INSERT_PROJECT_ACTIVITY_MORE_HOURS_REQUEST,
					variables: {
						projectActivityRequestedHours,
					},
				});
			} else {
				// Update existing hours request
				const obj = {
					hours: this.timeSelect,
					status: this.status,
					reason: this.reason,
				};

				await this.$apollo.mutate({
					mutation: UPDATE_PROJECT_ACTIVITY_REQUEST_MORE_HOURS,
					variables: {
						id: this.id,
						obj,
					},
				});
			}

			this.resetPage();
		} catch (error) {
			console.error('Error sending hours request:', error);
			this.$eventBus.emit(
				NOTIFICATION,
				Message.errorMessage('Failed to send hours request')
			);
		} finally {
			this.processing = false;
		}
	}
}

